import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { IconButton } from "components/iconButton";
import { format, formatISO, isEqual, startOfDay } from "date-fns";
import React from "react";
import { useAppSelector } from "store/hooks";
import { WeekDay, useCreateTravelEventMutation, useUpdateTravelEventMutation } from "store/reducers";
import { selectIsManager } from "store/reducers/config/configSlice";
import { selectTravelEventById } from "store/reducers/travelEvent";

import { LocationDropdown } from "../attendance/LocationDropdown";
import { TableCell } from "./table_components/table_cell";
import { TimeInput } from "./table_components/time_input";

type Props = {
  travelEventId: number;
};

export function getTravelEventSortTime(travelEvent: TravelEvent) {
  const start = new Date(travelEvent.travel_start_time);
  return travelEvent.is_manual && isEqual(start, startOfDay(start)) ? null : start.getTime();
}

export const TravelEvent: React.FC<Props> = (props) => {
  const { travelEventId } = props;
  const travelEvent = useAppSelector((state) => selectTravelEventById(state, travelEventId));
  const isManager = useAppSelector(selectIsManager);
  const [updateTravelEvent] = useUpdateTravelEventMutation();

  if (travelEvent === undefined) {
    return null;
  }

  const travelStartTime = new Date(travelEvent.travel_start_time);
  const startTimeWasNotRecorded = getTravelEventSortTime(travelEvent) === null;

  return (
    <tr
      key={travelEventId}
      style={{ height: "3.5em" }}
      className={classNames({
        "line-through": travelEvent.archived,
      })}
    >
      <TableCell>
        <FontAwesomeIcon icon="car-side" title="Travel" />
      </TableCell>
      <TableCell>{startTimeWasNotRecorded ? "Not recorded" : format(travelStartTime, "h:mm a")}</TableCell>
      <TableCell />
      <TableCell />
      <TableCell>1 unit</TableCell>
      <TableCell>Travel</TableCell>
      <TableCell>{travelEvent.travel_to_location.name}</TableCell>
      <TableCell>
        {isManager && !travelEvent.archived && (
          <IconButton
            icon="trash-alt"
            title="Archive travel"
            colour="danger"
            size="lg"
            onClick={() => updateTravelEvent({ travelEventId: travelEvent.id })}
          />
        )}
      </TableCell>
    </tr>
  );
};

type NewTravelEventRowProps = {
  employee: Employee;
  day: WeekDay;
  onCancel: () => void;
};

export function NewTravelEventRow({ employee, day, onCancel }: NewTravelEventRowProps) {
  const [travelStartTime, setTravelStartTime] = React.useState<Date | null>(null);
  const [toLocationId, setToLocationId] = React.useState<number>();
  const [createTravelEvent, { isLoading }] = useCreateTravelEventMutation();

  async function onSave() {
    if (!travelStartTime || !toLocationId) return;
    await createTravelEvent({
      employee_id: employee.id,
      travel_to_location_id: toLocationId,
      travel_start_time: formatISO(travelStartTime),
    });
    onCancel();
  }

  return (
    <tr>
      <TableCell>
        <FontAwesomeIcon icon="car-side" title="Travel" />
      </TableCell>
      <TableCell>
        <div className="absolute top-0 left-0 h-full w-full">
          <TimeInput
            type="timesheet"
            initialValue={travelStartTime}
            baseDate={new Date(day.date)}
            onEnter={setTravelStartTime}
            autoFocus
            disabled={isLoading}
          />
        </div>
      </TableCell>
      <TableCell />
      <TableCell />
      <TableCell>1 unit</TableCell>
      <TableCell>Travel</TableCell>
      <TableCell>
        <LocationDropdown
          value={toLocationId ?? ""}
          onChange={(event) => setToLocationId(Number(event.target.value) || undefined)}
          disabled={isLoading}
          emptyOptionLabel="Destination"
        />
      </TableCell>
      <TableCell>
        <IconButton
          icon="save"
          title="Save travel"
          colour="accent"
          size="lg"
          onClick={onSave}
          disabled={isLoading || !travelStartTime || !toLocationId}
        />
        <IconButton icon="times" title="Cancel" colour="danger" size="lg" onClick={onCancel} disabled={isLoading} />
      </TableCell>
    </tr>
  );
}
